import { computed, onBeforeUnmount, ref, watch, type Ref } from "vue";
import {
  DS_RESIZE_HANDLE_INPUT_SOURCE,
  type DSResizeHandleResizeEndDetails,
  type DSResizeHandleResizeStartDetails,
} from "@/src/design_system/primitives/resize_handle/types/emits";
import {
  type DSResizeHandleModelValue,
  type DSResizeHandleProps,
} from "@/src/design_system/primitives/resize_handle/types/props";
import { RTL_DIRECTION } from "@/src/design_system/shared/constants/direction";
import { DS_DIRECTION, type DSDirection } from "@/src/design_system/shared/types/direction";

interface DSResizeHandleCallbacks {
  onResizeStart: (details: DSResizeHandleResizeStartDetails) => boolean | void;
  onResizeEnd: (details: DSResizeHandleResizeEndDetails) => void;
}

interface UseDSResizeHandleOptions extends DSResizeHandleCallbacks {
  rootRef: Ref<HTMLElement | undefined>;
  props: Readonly<DSResizeHandleProps>;
  modelValue: Ref<DSResizeHandleModelValue>;
}

const KEYBOARD_DIRECTION_BY_DIRECTION: Record<DSDirection, Partial<Record<string, number>>> = {
  [DS_DIRECTION.Horizontally]: {
    ArrowUp: -1,
    ArrowDown: 1,
  },
  [DS_DIRECTION.Vertically]: {
    ArrowLeft: -1,
    ArrowRight: 1,
  },
};

const normalizeNumber = (value: number | undefined, fallback: number) =>
  Number.isFinite(value) ? (value as number) : fallback;

export const useDSResizeHandle = ({
  rootRef,
  props,
  modelValue,
  onResizeStart,
  onResizeEnd,
}: UseDSResizeHandleOptions) => {
  const isActive = ref(false);
  const normalizedDirection = computed<DSDirection>(() =>
    props.direction === DS_DIRECTION.Vertically ? DS_DIRECTION.Vertically : DS_DIRECTION.Horizontally
  );
  const normalizedMin = computed(() => normalizeNumber(props.min, 0));
  const normalizedMax = computed(() => Math.max(normalizedMin.value, normalizeNumber(props.max, 100)));
  const normalizedValue = computed(() => {
    const value = normalizeNumber(modelValue.value, normalizedMin.value);
    return Math.min(normalizedMax.value, Math.max(normalizedMin.value, value));
  });
  const normalizedStep = computed(() => {
    const step = normalizeNumber(props.step, 1);
    return step > 0 ? step : 1;
  });

  let activePointerId: number | null = null;
  let startCoordinate = 0;
  let startValue = 0;
  let lastDelta = 0;
  let lastEmittedValue = 0;

  const pointerCoordinate = (event: PointerEvent) =>
    normalizedDirection.value === DS_DIRECTION.Vertically ? event.clientX : event.clientY;

  const emitPointerResize = (event: PointerEvent) => {
    const delta = pointerCoordinate(event) - startCoordinate;
    lastDelta = delta;
    const value = Math.min(
      normalizedMax.value,
      Math.max(normalizedMin.value, startValue + delta * inlineDirectionMultiplier())
    );
    if (value === lastEmittedValue) return;

    lastEmittedValue = value;
    modelValue.value = value;
  };

  const releasePointer = () => {
    if (activePointerId === null) return;

    const pointerId = activePointerId;
    activePointerId = null;
    isActive.value = false;

    if (rootRef.value?.hasPointerCapture(pointerId)) {
      rootRef.value.releasePointerCapture(pointerId);
    }
  };

  const finishPointerResize = (canceled: boolean) => {
    if (activePointerId === null) return;

    const details: DSResizeHandleResizeEndDetails = {
      source: DS_RESIZE_HANDLE_INPUT_SOURCE.Pointer,
      delta: lastDelta,
      canceled,
    };
    releasePointer();
    onResizeEnd(details);
  };

  const onPointerDown = (event: PointerEvent) => {
    const isBlocked = [
      props.disabled,
      event.button !== 0,
      event.isPrimary === false,
      activePointerId !== null,
    ].includes(true);
    if (isBlocked) return;

    startCoordinate = pointerCoordinate(event);
    startValue = normalizedValue.value;
    lastDelta = 0;
    lastEmittedValue = startValue;
    const resizeAccepted = onResizeStart({
      source: DS_RESIZE_HANDLE_INPUT_SOURCE.Pointer,
      coordinate: startCoordinate,
      value: normalizedValue.value,
    });
    if (resizeAccepted === false) {
      event.preventDefault();
      return;
    }

    activePointerId = event.pointerId;
    isActive.value = true;
    rootRef.value?.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  const onPointerMove = (event: PointerEvent) => {
    if (event.pointerId !== activePointerId) return;

    emitPointerResize(event);
    event.preventDefault();
  };

  const onPointerUp = (event: PointerEvent) => {
    if (event.pointerId !== activePointerId) return;

    emitPointerResize(event);
    finishPointerResize(false);
    event.preventDefault();
  };

  const onPointerCancel = (event: PointerEvent) => {
    if (event.pointerId !== activePointerId) return;

    finishPointerResize(true);
  };

  const onLostPointerCapture = (event: PointerEvent) => {
    if (event.pointerId !== activePointerId) return;

    finishPointerResize(true);
  };

  const inlineDirectionMultiplier = () => {
    const root = rootRef.value;
    if (normalizedDirection.value !== DS_DIRECTION.Vertically || !root) return 1;

    return getComputedStyle(root).direction === RTL_DIRECTION ? -1 : 1;
  };

  const getKeyboardTargetValue = (key: string, currentValue: number) => {
    const boundaryValues: Partial<Record<string, number>> = {
      Home: normalizedMin.value,
      End: normalizedMax.value,
    };
    const boundaryValue = boundaryValues[key];
    if (boundaryValue !== undefined) return boundaryValue;

    const direction = KEYBOARD_DIRECTION_BY_DIRECTION[normalizedDirection.value][key];
    if (direction === undefined) return undefined;

    return currentValue + direction * inlineDirectionMultiplier() * normalizedStep.value;
  };

  const onKeydown = (event: KeyboardEvent) => {
    if (props.disabled || isActive.value) return;

    const currentValue = normalizedValue.value;
    const targetValue = getKeyboardTargetValue(event.key, currentValue);
    if (targetValue === undefined) return;

    event.preventDefault();
    const value = Math.min(normalizedMax.value, Math.max(normalizedMin.value, targetValue));
    if (value === currentValue) return;

    modelValue.value = value;
  };

  watch(
    () => props.disabled,
    disabled => {
      if (disabled) finishPointerResize(true);
    }
  );

  watch(normalizedDirection, () => finishPointerResize(true));

  onBeforeUnmount(() => finishPointerResize(true));

  return {
    isActive,
    normalizedDirection,
    normalizedMin,
    normalizedMax,
    normalizedValue,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    onLostPointerCapture,
    onKeydown,
  };
};
